import { BookingNumber, getBooleanLabel, RedeliveryNumber, StackUnitNumber } from 'components/tables/utils';

import { EquipmentType, Role, UnitRow } from 'types';
import { T_DataTableColumn } from 'types/data-table';

interface StackColumnType {
  id: T_DataTableColumn<UnitRow, 'number'>;
  deliveredDate: T_DataTableColumn<UnitRow, 'dateTime'>;
  receivedDate: T_DataTableColumn<UnitRow, 'dateTime'>;
  manufacturedDate: T_DataTableColumn<UnitRow, 'date'>;
  location: T_DataTableColumn<UnitRow, 'location'>;
  stack: T_DataTableColumn<UnitRow, 'stack'>;
  equipmentType: T_DataTableColumn<UnitRow, 'equipmentType'>;

  booking: T_DataTableColumn<UnitRow, 'booking'>;
  containerNumber: T_DataTableColumn<UnitRow, 'text'>;
  customerName: T_DataTableColumn<UnitRow, 'customer'>;
  customerFullName: T_DataTableColumn<UnitRow, 'customer'>;
  customerCode: T_DataTableColumn<UnitRow, 'customer'>;
  customerLocation: T_DataTableColumn<UnitRow, 'customer'>;
  containerCustomerName: T_DataTableColumn<UnitRow, 'customer'>;
  containerCustomerFullName: T_DataTableColumn<UnitRow, 'customer'>;
  containerCustomerCode: T_DataTableColumn<UnitRow, 'customer'>;
  containerCustomerLocation: T_DataTableColumn<UnitRow, 'customer'>;
  chassisCustomerName: T_DataTableColumn<UnitRow, 'customer'>;
  chassisCustomerFullName: T_DataTableColumn<UnitRow, 'customer'>;
  chassisCustomerCode: T_DataTableColumn<UnitRow, 'customer'>;
  chassisCustomerLocation: T_DataTableColumn<UnitRow, 'customer'>;
  gensetCustomerName: T_DataTableColumn<UnitRow, 'customer'>;
  gensetCustomerFullName: T_DataTableColumn<UnitRow, 'customer'>;
  gensetCustomerCode: T_DataTableColumn<UnitRow, 'customer'>;
  gensetCustomerLocation: T_DataTableColumn<UnitRow, 'customer'>;
  redelivery: T_DataTableColumn<UnitRow, 'redelivery'>;
  sizeType: T_DataTableColumn<UnitRow, 'sizeType'>;
  unitStatus: T_DataTableColumn<UnitRow, 'unitStatus'>;
  statusReason: T_DataTableColumn<UnitRow, 'text'>;

  chassisNumber: T_DataTableColumn<UnitRow, 'text'>;
  chassisCustomer: T_DataTableColumn<UnitRow, 'customer'>;
  chassisSizeType: T_DataTableColumn<UnitRow, 'sizeType'>;
  chassisStatus: T_DataTableColumn<UnitRow, 'unitStatus'>;
  chassisStatusReason: T_DataTableColumn<UnitRow, 'text'>;

  gensetNumber: T_DataTableColumn<UnitRow, 'text'>;
  gensetCustomer: T_DataTableColumn<UnitRow, 'customer'>;
  gensetSizeType: T_DataTableColumn<UnitRow, 'sizeType'>;
  gensetStatus: T_DataTableColumn<UnitRow, 'unitStatus'>;
  gensetStatusReason: T_DataTableColumn<UnitRow, 'text'>;

  unitGrade: T_DataTableColumn<UnitRow, 'text'>;
  onHold: T_DataTableColumn<UnitRow, 'boolean'>;
  holdNotes: T_DataTableColumn<UnitRow, 'text'>;
  notes: T_DataTableColumn<UnitRow, 'text'>;
  createdBy: T_DataTableColumn<UnitRow, 'text'>;
  flatbed: T_DataTableColumn<UnitRow, 'boolean'>;
  ownerChassis: T_DataTableColumn<UnitRow, 'boolean'>;
  licensePlate: T_DataTableColumn<UnitRow, 'text'>;
  fmcsaDate: T_DataTableColumn<UnitRow, 'date'>;
  licensePlateExpirationDate: T_DataTableColumn<UnitRow, 'date'>;
  hazard: T_DataTableColumn<UnitRow, 'boolean'>;
  loaded: T_DataTableColumn<UnitRow, 'boolean'>;
  seal0: T_DataTableColumn<UnitRow, 'text'>;
  seal1: T_DataTableColumn<UnitRow, 'text'>;
  seal2: T_DataTableColumn<UnitRow, 'text'>;
  billOfLading: T_DataTableColumn<UnitRow, 'text'>;
  weight: T_DataTableColumn<UnitRow, 'number'>;
  humidity: T_DataTableColumn<UnitRow, 'number'>;
  temperature: T_DataTableColumn<UnitRow, 'number'>;
  ventilation: T_DataTableColumn<UnitRow, 'text'>;
  carbCompliant: T_DataTableColumn<UnitRow, 'boolean'>;
  fuelLevel: T_DataTableColumn<UnitRow, 'number'>;
  daysInYard: T_DataTableColumn<UnitRow, 'number'>;
}

type StackDataTableColumn = { [P in keyof StackColumnType]: StackColumnType[P] }[keyof StackColumnType];

const stackDataTableColumns: StackDataTableColumn[] = [
  /* common columns */
  { field: 'id', label: 'RECORD', width: 80, category: 'common' },
  {
    field: 'receivedDate',
    label: 'GATE IN',
    type: 'dateTime',
    dateFormat: 'M/D/YY LT',
    width: 80,
    category: 'common'
  },
  {
    field: 'manufacturedDate',
    label: 'MANUFACTURED',
    type: 'date',
    dateFormat: 'M/D/YY',
    width: 80,
    category: 'common'
  },
  {
    field: 'redelivery',
    label: 'REDELIVERY',
    type: 'redelivery',
    getValueLabel: row => row?.redeliveryNumber,
    renderCell: row => <RedeliveryNumber redelivery={row} ADMIN BILLING_ADMIN BOOKING CUSTOMER EXTERNAL />,
    width: 80,
    category: 'common'
  },
  {
    field: 'booking',
    label: 'BOOKING',
    type: 'booking',
    getValueLabel: booking => booking?.bookingNumber,
    renderCell: booking => <BookingNumber booking={booking} ADMIN BILLING_ADMIN BOOKING CUSTOMER EXTERNAL />,
    width: 80,
    category: 'common'
  },
  { width: 144, field: 'customerName', label: 'ACCT', sortable: false, type: 'customer', category: 'common' },
  { width: 144, field: 'customerFullName', label: 'FULL ACCT', sortable: false, type: 'customer', category: 'common' },
  { width: 144, field: 'customerCode', label: 'ACCT CODE', sortable: false, type: 'customer', category: 'common' },
  { width: 144, field: 'customerLocation', label: 'ACCT LOC', sortable: false, type: 'customer', category: 'common' },
  {
    width: 80,
    field: 'location',
    label: 'LOCATION',
    type: 'location',
    getValueLabel: location => location?.name,
    category: 'common'
  },
  { width: 80, field: 'equipmentType', label: 'TYPE', category: 'common' },
  { width: 80, field: 'unitGrade', label: 'UNIT GRADE', category: 'common' },
  { width: 80, field: 'onHold', label: 'ON HOLD', type: 'boolean', getValueLabel: getBooleanLabel, category: 'common' },
  { width: 80, field: 'holdNotes', label: 'HOLD NOTES', category: 'common' },
  { width: 80, field: 'notes', label: 'NOTES', category: 'common' },
  {
    width: 80,
    field: 'createdBy',
    label: 'GATE USER',
    allowedRoles: [Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING],
    category: 'common'
  },
  { width: 80, field: 'sizeType', label: 'SIZE TYPE', category: 'common' },
  { width: 80, field: 'unitStatus', label: 'UNIT STATUS', category: 'common' },
  { width: 80, field: 'statusReason', label: 'STATUS NOTES', category: 'common' },
  { width: 80, field: 'daysInYard', label: 'DAYS IN YARD', type: 'number', category: 'common' },

  /* chassis-specific columns */
  {
    field: 'chassisNumber',
    label: 'CHASSIS',
    type: 'text',
    renderCell: (value, row) => (
      <StackUnitNumber
        unitNumber={value}
        stack={row.stack?.filter(({ equipmentType }) => equipmentType === EquipmentType.CHASSIS)}
        flatbed={row.flatbed}
        ownerChassis={row.ownerChassis}
        ADMIN
        BILLING_ADMIN
        ADMIN_CUSTOMER
        BOOKING
        SENIOR_BOOKING
        GATE_BOOKING
        CUSTOMER
        EXTERNAL
      />
    ),
    width: 80,
    category: 'chassis'
  },
  {
    width: 144,
    field: 'chassisCustomerName',
    label: 'CHAS ACCT',
    sortable: false,
    type: 'customer',
    category: 'chassis'
  },
  {
    width: 144,
    field: 'chassisCustomerFullName',
    label: 'CHAS FULL ACCT',
    sortable: false,
    type: 'customer',
    category: 'chassis'
  },
  {
    width: 144,
    field: 'chassisCustomerCode',
    label: 'CHAS ACCT CODE',
    sortable: false,
    type: 'customer',
    category: 'chassis'
  },
  {
    width: 144,
    field: 'chassisCustomerLocation',
    label: 'CHAS ACCT LOC',
    sortable: false,
    type: 'customer',
    category: 'chassis'
  },
  {
    width: 80,
    field: 'chassisCustomer',
    label: 'CHAS CUSTOMER',
    type: 'customer',
    getValueLabel: customer => customer?.name,
    category: 'chassis'
  },
  { width: 80, field: 'chassisSizeType', label: 'CHAS SIZE TYPE', category: 'chassis' },
  { width: 80, field: 'chassisStatus', label: 'CHAS STATUS', category: 'chassis' },
  { width: 80, field: 'chassisStatusReason', label: 'CHAS STATUS NOTES', category: 'chassis' },
  {
    width: 80,
    field: 'flatbed',
    label: 'FLATBED',
    type: 'boolean',
    getValueLabel: getBooleanLabel,
    category: 'chassis'
  },
  {
    width: 80,
    field: 'ownerChassis',
    label: 'OWNER',
    type: 'boolean',
    getValueLabel: getBooleanLabel,
    category: 'chassis'
  },
  { width: 80, field: 'licensePlate', label: 'LICENSE PLATE', category: 'chassis' },
  { width: 80, field: 'fmcsaDate', label: 'FMCSA', dateFormat: 'MM/YYYY', category: 'chassis' },
  {
    width: 80,
    field: 'licensePlateExpirationDate',
    label: 'LP EXP',
    dateFormat: 'MM/YYYY',
    category: 'chassis'
  },

  /* genset-specific columns */
  {
    field: 'gensetNumber',
    label: 'GENSET',
    type: 'text',
    renderCell: (value, row) => (
      <StackUnitNumber
        unitNumber={value}
        stack={row.stack?.filter(({ equipmentType }) => equipmentType === EquipmentType.GENSET)}
        ADMIN
        BILLING_ADMIN
        ADMIN_CUSTOMER
        BOOKING
        SENIOR_BOOKING
        GATE_BOOKING
        CUSTOMER
        EXTERNAL
      />
    ),
    width: 80,
    category: 'genset'
  },
  { width: 144, field: 'gensetCustomerName', label: 'GEN ACCT', sortable: false, type: 'customer', category: 'genset' },
  {
    width: 144,
    field: 'gensetCustomerFullName',
    label: 'GEN FULL ACCT',
    sortable: false,
    type: 'customer',
    category: 'genset'
  },
  {
    width: 144,
    field: 'gensetCustomerCode',
    label: 'GEN ACCT CODE',
    sortable: false,
    type: 'customer',
    category: 'genset'
  },
  {
    width: 144,
    field: 'gensetCustomerLocation',
    label: 'GEN ACCT LOC',
    sortable: false,
    type: 'customer',
    category: 'genset'
  },
  {
    width: 80,
    field: 'gensetCustomer',
    label: 'GENS CUSTOMER',
    type: 'customer',
    getValueLabel: customer => customer?.name,
    category: 'genset'
  },
  { width: 80, field: 'gensetSizeType', label: 'GENS SIZE TYPE', category: 'genset' },
  { width: 80, field: 'gensetStatus', label: 'GENS STATUS', category: 'genset' },
  { width: 80, field: 'gensetStatusReason', label: 'GENS STATUS NOTES', category: 'genset' },
  { width: 80, field: 'humidity', label: 'HUMIDITY', category: 'genset' },
  { width: 80, field: 'temperature', label: 'TEMPERATURE', category: 'genset' },
  { width: 80, field: 'ventilation', label: 'VENTILATION', category: 'genset' },
  {
    width: 80,
    field: 'carbCompliant',
    label: 'CARB',
    type: 'boolean',
    getValueLabel: getBooleanLabel,
    category: 'genset'
  },
  { width: 80, field: 'fuelLevel', label: 'FUEL', category: 'genset' },

  /* container-specific columns */
  {
    field: 'containerNumber',
    label: 'CONTAINER',
    type: 'text',
    renderCell: (value, row) => (
      <StackUnitNumber
        unitNumber={value}
        stack={row.stack?.filter(({ equipmentType }) => equipmentType === EquipmentType.CONTAINER)}
        ADMIN
        BILLING_ADMIN
        ADMIN_CUSTOMER
        BOOKING
        SENIOR_BOOKING
        GATE_BOOKING
        CUSTOMER
        EXTERNAL
      />
    ),
    width: 80,
    category: 'container'
  },
  {
    width: 144,
    field: 'containerCustomerName',
    label: 'CONT ACCT',
    sortable: false,
    type: 'customer',
    category: 'container'
  },
  {
    width: 144,
    field: 'containerCustomerFullName',
    label: 'CONT FULL ACCT',
    sortable: false,
    type: 'customer',
    category: 'container'
  },
  {
    width: 144,
    field: 'containerCustomerCode',
    label: 'CONT ACCT CODE',
    sortable: false,
    type: 'customer',
    category: 'container'
  },
  {
    width: 144,
    field: 'containerCustomerLocation',
    label: 'CONT ACCT LOC',
    sortable: false,
    type: 'customer',
    category: 'container'
  },
  {
    width: 80,
    field: 'hazard',
    label: 'HAZARD',
    type: 'boolean',
    getValueLabel: getBooleanLabel,
    category: 'container'
  },
  {
    width: 80,
    field: 'loaded',
    label: 'LOADED',
    type: 'boolean',
    getValueLabel: getBooleanLabel,
    category: 'container'
  },
  { width: 80, field: 'seal0', label: 'SEAL #1', category: 'container' },
  { width: 80, field: 'seal1', label: 'SEAL #2', category: 'container' },
  { width: 80, field: 'seal2', label: 'SEAL #3', category: 'container' },
  { width: 80, field: 'billOfLading', label: 'LADING', category: 'container' },
  { width: 80, field: 'weight', label: 'WEIGHT', category: 'container' }
];

export default stackDataTableColumns;

export const defaultStackColumns: Array<keyof StackColumnType> = [
  'id',
  'booking',
  'containerNumber',
  'chassisNumber',
  'gensetNumber',
  'receivedDate',
  'containerCustomerName',
  'sizeType',
  'unitStatus',
  'statusReason',
  'chassisCustomer',
  'chassisSizeType',
  'chassisStatus',
  'chassisStatusReason',
  'gensetCustomer',
  'gensetSizeType',
  'gensetStatus',
  'gensetStatusReason',
  'location',
  'equipmentType'
];
